// Contrast (the Mac's Settings ▸ Appearance ▸ High contrast and Yellow on black, `AppSettings.highContrast`/
// `yellowOnBlack`): black ink on white paper, or white on black in Lamplight, with stronger rules; or yellow ink on a
// black page whatever the colour scheme. The browser's own `prefers-contrast: more` turns high contrast on by itself
// (app.css); the choice here is forced for this browser, as `data-contrast` on the document, kept in localStorage.
import { useSyncExternalStore } from 'react';

export type Contrast = 'normal' | 'more' | 'yellow';

const KEY = 'ws:contrast';
const listeners = new Set<() => void>();

function stored(): Contrast {
  try {
    const value = localStorage.getItem(KEY);
    return value === 'more' || value === 'yellow' ? value : 'normal';
  } catch {
    return 'normal';
  }
}

/** Puts the choice on the document (at start-up, and whenever it changes). */
export function applyContrast(mode: Contrast = stored()) {
  if (mode === 'normal') delete document.documentElement.dataset.contrast;
  else document.documentElement.dataset.contrast = mode;
}

export function setContrast(mode: Contrast) {
  try {
    if (mode === 'normal') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, mode);
  } catch {
    // kept for this page only
  }
  applyContrast(mode);
  for (const fn of listeners) fn();
}

/** The contrast forced in this browser. */
export function useContrast(): Contrast {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    stored,
    () => 'normal' as const,
  );
}
